import { type RosterNoteDTO, VacationPolicy, dateAdd } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import "@pentacode/ui/components";
import autosize from "autosize";
import { css, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { alert, confirm } from "../../elements/alert-dialog";
import { Checkbox, type CheckboxButton } from "../../elements/checkbox";
import { DateInput } from "../../elements/date-input";
import "../../elements/entity-filters";
import type { EntityFiltersEl } from "../../elements/entity-filters";
import { Popover } from "../../elements/popover";
import "../../elements/scroller";
import { app } from "../../init";
import { ColorInput } from "../color-picker/color-input";
import { Textarea } from "../text-area/textarea";

@customElement("ptc-roster-note-popover")
export class RosterNotePopover extends Popover {
    @property({ attribute: false })
    rosterNote: RosterNoteDTO | null = null;

    @state()
    private _original?: Partial<RosterNoteDTO>;

    @state()
    private _loading = false;

    @query("form")
    private _form: HTMLFormElement;

    @query("[name='start']")
    private _startInput: DateInput;

    @query("[name='end']")
    private _endInput: DateInput;

    @query("[name='text']")
    private _textInput: HTMLTextAreaElement;

    @query("[name='color']")
    private _colorInput: ColorInput;

    @query("#filters")
    private _filtersInput: EntityFiltersEl;

    @query("[name='displayAsRosterNote']")
    private _displayAsRosterNoteCheckbox: CheckboxButton;

    @query("[name='vacationPolicy']")
    private _vacationPolicySelect: HTMLSelectElement;

    private get _data() {
        if (!this._form) {
            return;
        }

        const data = new FormData(this._form);
        const end = data.get("end") as DateString | null;
        return {
            start: data.get("start") as string,
            end: end && dateAdd(end, { days: 1 }),
            text: data.get("text") as string,
            color: (data.get("color") as string) || null,
            filters: this._filtersInput.filters,
            displayAsRosterNote: data.has("displayAsRosterNote"),
            vacationPolicy: data.get("vacationPolicy") as VacationPolicy,
        };
    }

    updated(changes: Map<string, unknown>) {
        if (changes.has("rosterNote")) {
            this._original = { ...this.rosterNote };
            this._fill();
            this._textInput?.focus();
        }
    }

    hide(edited = false, deleted = false) {
        if (this.rosterNote && this._original) {
            Object.assign(this.rosterNote, this._original);
        }
        this.dispatchEvent(new CustomEvent("change"));
        this.dispatchEvent(new CustomEvent("done", { detail: { deleted: deleted || !this.rosterNote?.id, edited } }));
        super.hide();
    }

    async connectedCallback() {
        super.connectedCallback();
        await this.updateComplete;
        autosize(this._textInput);
    }

    disconnectedCallback() {
        // Unregister from autosize so the detached <textarea> and its listeners can be collected.
        if (this._textInput) {
            autosize.destroy(this._textInput);
        }
        super.disconnectedCallback();
    }

    private _fill() {
        if (!this.rosterNote) {
            return;
        }
        this._startInput.value = this.rosterNote.start;
        this._endInput.value = this.rosterNote.end ? dateAdd(this.rosterNote.end, { days: -1 }) : null;
        this._textInput.value = this.rosterNote.text || "";
        this._colorInput.value = this.rosterNote.color || "";
        this._filtersInput.filters = this.rosterNote.filters;
        this._vacationPolicySelect.value = this.rosterNote.vacationPolicy;
        this._displayAsRosterNoteCheckbox.checked = this.rosterNote.displayAsRosterNote;
        autosize.update(this._textInput);
    }

    private async _submit(e: Event) {
        e.preventDefault();

        if (!this.rosterNote) {
            return;
        }

        this._loading = true;

        try {
            const note = await app.api.createOrUpdateRosterNote(this.rosterNote);
            Object.assign(this.rosterNote, note);
            this._original = undefined;
            this.hide(true);
        } catch (e) {
            await alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _deleteRosterNote() {
        setTimeout(() => (this._ignoreNextClick = true), 50);
        if (
            !(await confirm("Wollen Sie diesen Kalendereintrag wirklich entfernen?", "Entfernen", "Abbrechen", {
                title: "Kalendereintrag Entfernen",
                type: "destructive",
                icon: "icon.object.note",
            }))
        ) {
            return;
        }

        this._loading = true;

        try {
            if (this.rosterNote?.id) {
                await app.api.deleteRosterNote(this.rosterNote.id);
                this.hide(false, true);
            }
        } catch (e) {
            await alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _applyChanges() {
        Object.assign(this.rosterNote!, this._data);
        this._endInput.min = this._startInput.value || undefined;
        if (this._form.checkValidity()) {
            this.dispatchEvent(new CustomEvent("change"));
        }
        this.requestUpdate();
    }

    static styles = [
        ...Popover.styles,
        DateInput.styles,
        ColorInput.styles,
        Checkbox.styles,
        Textarea.styles,
        css`
            :host {
                display: block;
                width: 20em;
                --hover-buffer: 0;
                padding: 0;
            }

            .checkbox-grid {
                display: grid;
                grid-gap: 0.5em;
                grid-template-columns: repeat(auto-fit, minmax(10em, 1fr));
            }
        `,
    ];

    renderContent() {
        if (!this.rosterNote) {
            return html``;
        }
        const editing = !!this.rosterNote.id;

        return html`
            <form @submit=${this._submit} class="vertical layout fit-vertically">
                <div class="padded center-aligning horizontal spacing layout border-bottom">
                    <div class="semibold stretch">
                        <ptc-ui-icon name="icon.object.date"></ptc-ui-icon> ${editing
                            ? "Kalendereintrag Bearbeiten"
                            : "Neuer Kalendereintrag"}
                    </div>
                    <div ?hidden=${!editing}>
                        <button type="button" class="red skinny transparent" @click=${this._deleteRosterNote}>
                            <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                        </button>
                    </div>
                </div>

                <div class="padded">
                    <div class="vertical layout relative">
                        <ptc-textarea
                            name="text"
                            @input=${this._applyChanges}
                            placeholder="Geben Sie hier Ihre Notiz ein..."
                        ></ptc-textarea>
                    </div>

                    <div class="smaller horizontal spacing layout">
                        <div class="vertical layout stretch">
                            <label>Von</label>
                            <ptc-date-input
                                name="start"
                                required
                                @input=${this._applyChanges}
                                datePicker="popover"
                            ></ptc-date-input>
                        </div>
                        <div class="vertical layout stretch">
                            <label>Bis</label>
                            <ptc-date-input
                                name="end"
                                required
                                @input=${this._applyChanges}
                                datePicker="popover"
                            ></ptc-date-input>
                        </div>
                        <div class="vertical layout">
                            <label>Farbe</label>
                            <ptc-color-input class="huger" name="color" @change=${this._applyChanges}></ptc-color-input>
                        </div>
                    </div>

                    <div>
                        <label class="smaller">Arbeitsbereiche</label>

                        <ptc-entity-filters
                            id="filters"
                            .filterTypes=${["venue", "department", "position"] as const}
                            class="focus-container"
                            hideEmployeeCount
                            hideFilterIcon
                            @change=${this._applyChanges}
                            .emptyLabel=${"Alle Arbeitsbereiche"}
                        ></ptc-entity-filters>
                    </div>

                    <div>
                        <label class="smaller">Anzeige</label>
                        <ptc-checkbox-button
                            name="displayAsRosterNote"
                            buttonClass="smaller slim ghost"
                            .label=${"Als Dienstplannotiz Anzeigen"}
                            @change=${this._applyChanges}
                        ></ptc-checkbox-button>
                    </div>

                    <div>
                        <label class="smaller">Urlaubssperre</label>
                        <select name="vacationPolicy" @change=${this._applyChanges} class="smaller fill-horizontally">
                            <option value="${VacationPolicy.AllowAll}">Keine Urlaubssperre</option>
                            <option value="${VacationPolicy.DisallowAll}">Urlaube Komplett Sperren</option>
                            <option value="${VacationPolicy.DisallowRequests}">Nur Urlaubsanträge Sperren</option>
                        </select>
                    </div>

                    <div class="evenly stretching spacing horizontal layout smaller top-margined">
                        <button class="primary">Speichern</button>
                        <button class="transparent" type="button" @click=${() => this.hide()}>Abbrechen</button>
                    </div>
                </div>
            </form>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
